import { useCallback, useEffect } from 'react';
import {
  closeSnackbarNotification,
  removeSnackbarNotification,
} from '@slices/snackbar-notification';
import { useSnackbar } from 'notistack';

import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';

import { SnackbarNotificationKey } from '@types';

import { snackbarNotificationGetList } from '@selectors/snackbar-notification';

import { useTypedDispatch, useTypedSelector } from '../store';

let displayed: SnackbarNotificationKey[] = [];

export const useNotifier = (): void => {
  const dispatch = useTypedDispatch();
  const notifications = useTypedSelector(snackbarNotificationGetList);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const storeDisplayed = (id: SnackbarNotificationKey) => {
    displayed = [...displayed, id];
  };

  const removeDisplayed = (id: SnackbarNotificationKey) => {
    displayed = [...displayed.filter((key) => id !== key)];
  };

  const handleCloseSnackbar: React.MouseEventHandler<HTMLButtonElement> =
    useCallback(
      (event) => {
        const { key } = event.currentTarget.dataset;
        // remove this snackbar-notification from redux store
        dispatch(closeSnackbarNotification({ key }));
        removeDisplayed(key as SnackbarNotificationKey);
      },
      [dispatch]
    );

  useEffect(() => {
    notifications.forEach(
      ({ key, message, options = {}, dismissed = false }) => {
        if (dismissed) {
          // dismiss snackbar-notification using notistack
          closeSnackbar(key);

          return;
        }

        // do nothing if snackbar-notification is already displayed
        if (displayed.includes(key as SnackbarNotificationKey)) {
          return;
        }

        // display snackbar-notification using notistack
        enqueueSnackbar(message, {
          key,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          action: (key) => (
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              data-key={key}
              onClick={handleCloseSnackbar}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
          ...options,
          onClose: (event, reason, myKey) => {
            if (options.onClose) {
              options.onClose(event, reason, myKey);
            }
          },
          onExited: (event, myKey) => {
            // remove this snackbar-notification from redux store
            dispatch(
              removeSnackbarNotification({
                key: myKey as SnackbarNotificationKey,
              })
            );
            removeDisplayed(myKey as SnackbarNotificationKey);
          },
        });

        // keep track of snackbars that we've displayed
        storeDisplayed(key as SnackbarNotificationKey);
      }
    );
  }, [
    notifications,
    closeSnackbar,
    enqueueSnackbar,
    dispatch,
    handleCloseSnackbar,
  ]);
};
